/* eslint-disable react-hooks/exhaustive-deps */
import { useGlobalStore } from "@/store/GlobalStore";
import { Form, Tooltip } from "@renderbus/ui";
import { ValidatorRule } from "rc-field-form/lib/interface";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useForm from "antd/es/form/hooks/useForm";
import { initVerifyRechargeInfo, useAnimationTopUpStore } from "../../store";
import { ChangeCouponCode, CouponCodeTipWrapper, RechargeForm } from "./atom";
import { Input, Space } from "task-eva-ui";
import { CommonTitle } from "../../atom";
import CloseIcon from "@/assets/icons/topup/close-code";
import InfoIcon from "@/assets/icons/topup/info";
import {
  CouponCodeTipList,
  TipType,
} from "../../constants/animation-balance-info";
import { couponTypeTranslate } from "@/components/top-up/help/couponTypeTranslate";

export interface IForm {
  amount: number;
}

export function RechargeInputForm() {
  const { t } = useTranslation("topup");
  const { currencySymbol } = useGlobalStore();
  const amount = useAnimationTopUpStore((state) => state.amount);
  const { setter: setAnimationTopUpStore } = useAnimationTopUpStore();
  const [currentInputAmount, setCurrentInputAmount] = useState("");
  const [form] = useForm<IForm>();
  const verifyRechargeInfo = useAnimationTopUpStore(
    (state) => state.verifyRechargeInfo
  );
  const { couponNO, rechargeAmount, couponAmount, couponType } =
    verifyRechargeInfo;

  const couponCodeTip = useMemo(
    () =>
      amount >= Number(rechargeAmount)
        ? CouponCodeTipList[TipType.Satisfied]
        : CouponCodeTipList[TipType.NotSatisfied],
    [amount, CouponCodeTipList]
  );

  const validator: ValidatorRule["validator"] = (_, value) => {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return Promise.reject(t("general_check_number"));
    } else if (!num) {
      return Promise.reject(t("general_check_must"));
    } else if (num < 0) {
      return Promise.reject(t("general_check_int_number"));
    } else {
      return Promise.resolve();
    }
  };

  const limitAmount = (value: any) => {
    if (!value || value === "0") return value;
    const amount = value.replace(
      // eslint-disable-next-line no-useless-escape
      /^(\-)*(\d+)\.(\d\d).*$/,
      "$1$2.$3"
    );
    const tempData = [...amount.toString()];
    if (!/^[^.]/.test(amount)) {
      tempData.unshift("0");
      setCurrentInputAmount(tempData.join(""));
      return tempData.join("");
    } else if (/^0\d*$/.test(amount) && amount.length > 1) {
      tempData.shift();
      setCurrentInputAmount(tempData.join(""));
      return tempData.join("");
    } else if (amount > 200000) {
      return currentInputAmount;
    } else if (amount && amount === "0.00") {
      tempData.pop();
      setCurrentInputAmount(tempData.join(""));
      return tempData.join("");
    } else {
      setCurrentInputAmount(amount);
      return amount;
    }
  };

  const limitKeyDown = (e: any) => {
    if (
      !(
        (e.key >= "0" && e.key <= "9") ||
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "." ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      )
    ) {
      e.preventDefault();
      return false;
    }
    return true;
  };

  const onInput = (val: any) => {
    setAnimationTopUpStore({
      amount: val,
      isCheckCard: false,
    });
  };

  const handleChange = (val: string) => {
    setAnimationTopUpStore({
      inputCode: "",
      verifyRechargeInfo: initVerifyRechargeInfo,
    });
  };

  const preventWheel = (e: any) => {
    e.preventDefault();
  };

  const onPasteChange = (event: any) => {
    event.preventDefault();
  };

  useEffect(() => {
    form.setFieldsValue({ amount: amount });
  }, [amount]);

  useEffect(() => {
    // 防止滚轮滚动的自增自减
    const inputElement = document.querySelector(
      ".animation-amount-input input"
    );
    if (inputElement) {
      inputElement.addEventListener("wheel", preventWheel, { passive: false });
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("wheel", preventWheel);
      }
    };
  }, []);

  return (
    <RechargeForm form={form}>
      <CommonTitle>{t("topup_other_amount")}</CommonTitle>
      <Space gap={14} align={"baseline"}>
        <Form.Item
          name="amount"
          rules={[{ validator }]}
          getValueFromEvent={(event: any) => {
            return limitAmount(event.target.value);
          }}
        >
          <div className="animation-amount-input">
            <Input
              prefix={currencySymbol}
              type={"number"}
              value={amount}
              background="#212327"
              pattern="[0-9]*"
              onKeyDown={limitKeyDown}
              onPaste={onPasteChange}
              onInput={(e) => onInput?.(limitAmount(e.currentTarget.value))}
              placeholder={t("general_enter_amount")}
            />
          </div>
        </Form.Item>
        {couponNO && (
          <ChangeCouponCode>
            <div>{couponNO}</div>
            <div className="change-btn" onClick={() => handleChange(couponNO)}>
              <CloseIcon />
            </div>
          </ChangeCouponCode>
        )}
      </Space>
      {couponNO && (
        <>
          <CouponCodeTipWrapper
            bg={couponCodeTip.bgColor}
            titleColor={couponCodeTip.titleColor}
          >
            {couponCodeTip.icon}
            <span className="pre-title">{t(couponCodeTip.preTitle)}</span>
            <span className="gift-tip">{t("renderbus_coupon_tip_gift")}</span>
            <span>
              {t("renderbus_coupon_tip_amount", {
                amount: rechargeAmount,
                coupon: couponAmount,
                couponType: couponTypeTranslate(couponType || 3),
              })}
            </span>
          </CouponCodeTipWrapper>
          <Tooltip
            title={t("renderbus_coupon_tooltip")}
            color={"#2c2e32"}
            tooltipText={<InfoIcon />}
          />
        </>
      )}
    </RechargeForm>
  );
}
